import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Trash2, Mail, X, Copy, CheckCircle2, Loader2, ShieldCheck, KeyRound } from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  revoked: "bg-zinc-100 text-zinc-700 border-zinc-200",
  expired: "bg-red-100 text-red-700 border-red-200",
};

export default function Users() {
  const { t } = useTranslation("users");
  const { t: tc } = useTranslation("common");
  const { user } = useAuth();
  const { slug } = useParams();
  const isSuperadmin = user?.role === "superadmin";
  const [orgId, setOrgId] = useState(isSuperadmin ? null : user?.organization_id);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [recentLink, setRecentLink] = useState(null);

  useEffect(() => {
    if (!isSuperadmin || !slug) return;
    api.get("/admin/organizations")
      .then((r) => {
        const o = (r.data || []).find((x) => x.slug === slug);
        if (o) setOrgId(o.id);
      })
      .catch(() => {});
  }, [isSuperadmin, slug]);

  const refresh = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        api.get(`/organizations/${orgId}/users`),
        api.get(`/organizations/${orgId}/invitations`),
      ]);
      setUsers(u.data);
      setInvites(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (orgId) refresh(); /* eslint-disable-next-line */ }, [orgId]);

  const revoke = async (id) => {
    if (!window.confirm(t("revokeConfirm"))) return;
    await api.post(`/invitations/${id}/revoke`);
    refresh();
  };

  const removeUser = async (id) => {
    if (!window.confirm(t("removeConfirm"))) return;
    await api.delete(`/organizations/${orgId}/users/${id}`);
    refresh();
  };

  const resetUserPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/organizations/${orgId}/users/${u.id}/reset-password`);
      const link = `${window.location.origin}/reset-password?token=${data.dev_token}`;
      setRecentLink(link);
    } catch (err) {
      window.alert("Erro: " + (err.response?.data?.detail || "not possible"));
    }
  };

  const roleLabel = (r) => t(`roles.${r}`, r);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="users-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle")}</p>
        </div>
        <button
          data-testid="invite-user-btn"
          onClick={() => setShowInvite(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          <UserPlus className="w-4 h-4" /> {t("invite")}
        </button>
      </div>

      {recentLink && (
        <div data-testid="recent-invite-link" className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <div className="text-sm font-medium text-amber-900 flex items-center gap-2"><Mail className="w-4 h-4" /> {t("invitationModal.copyLink")}</div>
          <div className="flex gap-2 items-center">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded px-2 py-1.5 break-all">{recentLink}</code>
            <button onClick={() => { navigator.clipboard.writeText(recentLink); }} className="p-1.5 rounded hover:bg-amber-100" title={tc("actions.copy")}><Copy className="w-4 h-4" /></button>
            <button onClick={() => setRecentLink(null)} className="p-1.5 rounded hover:bg-amber-100"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">{tc("states.loading")}</div>
      ) : (
        <>
          <section className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-zinc-700" />
              <h2 className="text-sm font-semibold text-zinc-900">{t("usersTab")} ({users.length})</h2>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">{t("table.name")}</th>
                  <th className="px-4 py-2.5 text-left font-medium">{t("table.email")}</th>
                  <th className="px-4 py-2.5 text-left font-medium">{t("table.role")}</th>
                  <th className="px-4 py-2.5 text-right font-medium">{t("table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-zinc-100 last:border-0">
                    <td className="px-4 py-2.5 text-zinc-900">{u.name}</td>
                    <td className="px-4 py-2.5 text-zinc-700">{u.email}</td>
                    <td className="px-4 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-zinc-100 text-zinc-700 border-zinc-200">
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {u.id !== user.id && (
                        <div className="inline-flex gap-1">
                          <button
                            data-testid={`reset-pw-${u.email}`}
                            onClick={() => resetUserPassword(u)}
                            className="text-xs text-zinc-700 hover:bg-zinc-100 px-2 py-1 rounded inline-flex items-center gap-1"
                          >
                            <KeyRound className="w-3.5 h-3.5" /> Reset PW
                          </button>
                          <button
                            data-testid={`remove-user-${u.email}`}
                            onClick={() => removeUser(u.id)}
                            className="text-xs text-red-700 hover:bg-red-50 px-2 py-1 rounded inline-flex items-center gap-1"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> {tc("actions.remove")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500 text-sm">{t("empty")}</td></tr>
                )}
              </tbody>
            </table>
          </section>

          <section className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
              <Mail className="w-4 h-4 text-zinc-700" />
              <h2 className="text-sm font-semibold text-zinc-900">{t("invitationsTab")} ({invites.length})</h2>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">{t("table.email")}</th>
                  <th className="px-4 py-2.5 text-left font-medium">{t("table.role")}</th>
                  <th className="px-4 py-2.5 text-left font-medium">{t("table.status")}</th>
                  <th className="px-4 py-2.5 text-left font-medium">{tc("labels.date")}</th>
                  <th className="px-4 py-2.5 text-right font-medium">{t("table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} data-testid={`invite-row-${inv.email}`} className="border-b border-zinc-100 last:border-0">
                    <td className="px-4 py-2.5 text-zinc-900">{inv.email}</td>
                    <td className="px-4 py-2.5 text-zinc-700">{roleLabel(inv.role)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE[inv.status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs">{(inv.expires_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {inv.status === "pending" && (
                        <button
                          data-testid={`revoke-${inv.email}`}
                          onClick={() => revoke(inv.id)}
                          className="text-xs text-red-700 hover:bg-red-50 px-2 py-1 rounded inline-flex items-center gap-1"
                        >
                          <X className="w-3.5 h-3.5" /> {t("revoke")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {invites.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-500 text-sm">{t("empty")}</td></tr>
                )}
              </tbody>
            </table>
          </section>
        </>
      )}

      {showInvite && (
        <InviteModal
          orgId={orgId}
          onClose={() => setShowInvite(false)}
          onCreated={(link) => {
            setShowInvite(false);
            setRecentLink(link);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function InviteModal({ orgId, onClose, onCreated }) {
  const { t } = useTranslation("users");
  const { t: tc } = useTranslation("common");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("gestor");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const { data } = await api.post(`/organizations/${orgId}/invitations`, { email, role });
      const link = `${window.location.origin}/accept-invitation?token=${data.dev_token}`;
      onCreated(link);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || tc("errors.generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="invite-modal" className="bg-white rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="text-lg font-display font-semibold text-zinc-900">{t("invitationModal.title")}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {error && <div data-testid="invite-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("invitationModal.email")}</label>
            <input data-testid="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm" placeholder="pessoa@agencia.pt" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("invitationModal.role")}</label>
            <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm">
              <option value="admin">{t("roles.admin")}</option>
              <option value="gestor">{t("roles.gestor")}</option>
              <option value="viewer">{t("roles.viewer")}</option>
            </select>
            <p className="text-xs text-zinc-500">{t(`roleDescriptions.${role}`)}</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-zinc-200 rounded-lg text-sm">{tc("actions.cancel")}</button>
            <button data-testid="invite-submit" type="submit" disabled={saving} className="px-4 py-2 bg-zinc-950 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {saving ? t("invitationModal.sending") : t("invitationModal.send")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
